import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calendar, Save, X } from 'lucide-react';
import { useVacationRequests } from '../../hooks/useVacationRequests';
import { toast } from 'sonner';
import { utcToChileanInputDate, chileanInputDateToISO } from '../../utils/dateUtils';

export const EditVacationRequestDialog = ({ request, open, onOpenChange, onSuccess }) => {
  const { updateRequest } = useVacationRequests();
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    type: 'vacation'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (request && open) {
      setFormData({
        startDate: utcToChileanInputDate(request.startDate),
        endDate: utcToChileanInputDate(request.endDate),
        reason: request.reason || '',
        type: request.type || 'vacation'
      });
    }
  }, [request, open]);

  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate + 'T00:00:00.000Z');
    const end = new Date(endDate + 'T00:00:00.000Z');
    
    if (start > end) return 0;
    
    let workingDays = 0;
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getUTCDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // No es domingo (0) ni sábado (6)
        workingDays++;
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }
    
    return workingDays;
  };

  const workingDays = calculateWorkingDays(formData.startDate, formData.endDate);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.startDate || !formData.endDate) {
      toast.error('Por favor completa todas las fechas');
      return;
    }

    if (new Date(chileanInputDateToISO(formData.startDate)) > new Date(chileanInputDateToISO(formData.endDate))) {
      toast.error('La fecha de inicio no puede ser posterior a la fecha de fin');
      return;
    }

    if (workingDays <= 0) {
      toast.error('Debe seleccionar al menos un día laboral');
      return;
    }

    setLoading(true);
    try {
      await updateRequest(request._id || request.id, {
        startDate: chileanInputDateToISO(formData.startDate),
        endDate: chileanInputDateToISO(formData.endDate),
        reason: formData.reason,
        type: formData.type,
        totalDays: workingDays
      });

      toast.success('Solicitud actualizada correctamente');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Error al actualizar la solicitud');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={20} />
            Editar Solicitud de {formData.type === 'vacation' ? 'Vacaciones' : 'Día Administrativo'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date">Fecha de Inicio</Label>
              <Input
                id="start-date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="end-date">Fecha de Fin</Label>
              <Input
                id="end-date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                required
              />
            </div>
          </div>

          {workingDays > 0 && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Días laborales seleccionados:</strong> {workingDays} día{workingDays !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="type">Tipo de Solicitud</Label>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="vacation">Vacaciones</option>
              <option value="administrative">Día Administrativo</option>
            </select>
          </div>

          <div>
            <Label htmlFor="reason">Motivo (opcional)</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              placeholder="Describe el motivo de la solicitud..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Guardar Cambios
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              <X size={16} className="mr-2" />
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
